import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, defer, firstValueFrom, from, of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { HelperService } from './helper.service';
import { environment } from 'src/environments/environment';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { agGridPreferences, decodeGridState, defaultGridView, gridStatePayload } from '../_helpers/grid-state';

const API_GRID_STATES_URL = environment.baseUrl + '/gridstates';

/** One persistence boundary for named views, legacy tables and AG Grid. */
@Injectable()
export class GridStatesService {
  private reportedErrors = new WeakSet<object>();
  private pending = new Map<string, Promise<any>>();
  private createdIds = new WeakMap<object, { owner: string; id: number }>();

  constructor(private http: HttpClient, private helperService: HelperService,
    private toastr: ToastrService, private translate: TranslateService) {}

  getAll(gridId: string): Observable<any[]> {
    const owner = this.helperService.filterOwner;
    return this.http.get<any[]>(`${API_GRID_STATES_URL}/${encodeURIComponent(gridId)}`,
      { headers: this.helperService.getHTTPHeaders() }).pipe(
      map(views => {
        if (owner !== this.helperService.filterOwner) throw new Error('Account changed while loading grid preferences');
        return (views || []).map(view => ({ ...view,
          gridColumns: JSON.stringify(decodeGridState(view.gridColumns)) }));
      }),
      catchError(error => this.failed(error)));
  }

  // Legacy callers use either a columns array or a { columns, filters } object.
  get(gridId: string, name: string = null): Observable<any> {
    return this.getAll(gridId).pipe(map(views => {
      const view = name ? views.find(item => item.name === name) : defaultGridView(views);
      if (!view) return null;
      const state = decodeGridState(view.gridColumns);
      return Object.keys(state).every(key => key === 'columns') ? state.columns : state;
    }), catchError(() => of(null)));
  }

  set(view: any, columns?: any[]): Observable<any> {
    const payload = gridStatePayload(view, columns);
    const owner = this.helperService.filterOwner;
    return this.enqueue(payload.gridId, async () => {
      const created = this.createdIds.get(view);
      if (!payload.id && created?.owner === owner) payload.id = created.id;
      const result = await this.write(payload);
      this.createdIds.set(view, { owner, id: result.id });
      return { ...result, gridColumns: decodeGridState(result.gridColumns) };
    });
  }

  /** Merge just the changed layout into the server view; keep saved filters intact. */
  saveColumns(gridId: string, columns: any[]): Observable<any> {
    const snapshot = decodeGridState(gridStatePayload({ gridId }, columns).gridColumns).columns;
    return this.saveDefault(gridId, state => ({ ...state, columns: snapshot }));
  }

  saveAgGrid(gridId: string, state: any): Observable<any> {
    const snapshot = agGridPreferences(state);
    return this.saveDefault(gridId, () => snapshot);
  }

  savePreferences(gridId: string, preferences: any): Observable<any> {
    const snapshot = decodeGridState(preferences);
    return this.saveDefault(gridId, state => ({ ...state, ...snapshot }));
  }

  private saveDefault(gridId: string, change: (state: any) => any): Observable<any> {
    const owner = this.helperService.filterOwner;
    return this.enqueue(gridId, async () => {
      // Read inside the queue so consecutive first saves cannot create duplicates.
      const views = await firstValueFrom(this.getAll(gridId));
      if (owner !== this.helperService.filterOwner) throw new Error('Account changed before saving grid preferences');
      const view = defaultGridView(views) || { gridId, isDefault: true };
      const payload = gridStatePayload({ ...view, gridColumns: change(decodeGridState(view.gridColumns)) });
      return this.write(payload);
    });
  }

  delete(id: number, gridId?: string): Observable<any> {
    return this.enqueue(gridId || `view:${id}`, () => firstValueFrom(
      this.http.delete(`${API_GRID_STATES_URL}/${id}`)));
  }

  private async write(payload: any): Promise<any> {
    const response: any = await firstValueFrom(payload.id
      ? this.http.put(API_GRID_STATES_URL, payload)
      : this.http.post(API_GRID_STATES_URL, payload));
    if (!response?.id) throw new Error('Saved grid response is missing its id');
    return response;
  }

  /** Serialize per account/grid. Unsubscribing must not cancel a dispatched write. */
  private enqueue<T>(gridId: string, operation: () => Promise<T>): Observable<T> {
    const owner = this.helperService.filterOwner;
    const key = JSON.stringify([owner, gridId]);
    return defer(() => {
      const previous = this.pending.get(key) || Promise.resolve();
      const request = previous.then(() => {
        if (!owner || this.helperService.filterOwner !== owner) {
          throw new Error('Account changed before grid preferences were saved');
        }
        return operation();
      });
      const settled = request.then(() => undefined, () => undefined);
      this.pending.set(key, settled);
      settled.then(() => { if (this.pending.get(key) === settled) this.pending.delete(key); });
      return from(request);
    }).pipe(catchError(error => this.failed(error)), shareReplay({ bufferSize: 1, refCount: false }));
  }

  selectedViewId(gridId: string): number | undefined {
    if (!this.helperService.filterOwner) return undefined;
    try {
      return Number(localStorage.getItem(this.helperService.filterStorageKey(`grid:${gridId}:selected`))) || undefined;
    } catch { return undefined; }
  }

  rememberView(gridId: string, id: number): void {
    if (!gridId || !id || !this.helperService.filterOwner) return;
    try { localStorage.setItem(this.helperService.filterStorageKey(`grid:${gridId}:selected`), String(id)); } catch {}
  }

  private failed(error: any): Observable<never> {
    if (error && typeof error === 'object') {
      if (!this.reportedErrors.has(error)) this.toastr.error(this.translate.instant('GENERAL.MESSAGES.ERROR'));
      this.reportedErrors.add(error);
    } else {
      this.toastr.error(this.translate.instant('GENERAL.MESSAGES.ERROR'));
    }
    return throwError(() => error);
  }
}
